import { Modal, NewButton, NewButtonVariant } from "@visiblethread/react-components";
import Flex, { GAP_CLASS } from "components/Flex";
import { ALIGN_CLASS, DIRECTION_CLASS } from "components/Flex/Flex";
import { NO_GATE, QUESTION_TYPE } from "constants/proposals";
import { modalTypes } from "constants/types";
import { arrayOf, number, oneOf, shape, string } from "prop-types";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { hideModal } from "slices/app/actions/modals";

import classes from "./GatePreviewModal.module.scss";

const SETTINGS_PATH = "/settings/pipeline-stages";

const TYPE_LABELS = {
  [QUESTION_TYPE.YES_NO]: "Yes / No",
  [QUESTION_TYPE.MULTIPLE_CHOICE]: "Multiple choice",
  [QUESTION_TYPE.FREE_TEXT]: "Text answer",
};

const GATE_MODE_LEGEND = [
  { mode: "SOFT_GATE", title: "Soft Gate", description: "Answer the questions or skip with a note." },
  { mode: "HARD_GATE", title: "Hard Gate", description: "Every question must be answered." },
  { mode: NO_GATE, title: "No Gate", description: "Cards move freely with no questions." },
];

const GatePreviewModal = ({ fromTitle, toTitle = null, questions = [], gateMode }) => {
  const dispatch = useDispatch();
  const close = () => dispatch(hideModal(modalTypes.GATE_PREVIEW_MODAL));
  const transitionTitle = toTitle ? `${fromTitle} → ${toTitle}` : fromTitle;

  const renderBody = () => {
    if (gateMode === NO_GATE) {
      return (
        <Flex
          direction={DIRECTION_CLASS.column}
          alignItems={ALIGN_CLASS.stretch}
          gap={GAP_CLASS.GAP_300}
        >
          <p className={classes["intro"]}>Gate questions are turned off for this board.</p>
          <Flex
            direction={DIRECTION_CLASS.column}
            alignItems={ALIGN_CLASS.stretch}
            gap={GAP_CLASS.GAP_200}
          >
            {GATE_MODE_LEGEND.map((entry) => (
              <Flex
                key={entry.mode}
                direction={DIRECTION_CLASS.column}
                alignItems={ALIGN_CLASS.stretch}
                gap={GAP_CLASS.GAP_050}
                className={
                  entry.mode === gateMode
                    ? classes["legend__entry--current"]
                    : classes["legend__entry"]
                }
              >
                <span className={classes["legend__title"]}>{entry.title}</span>
                <span className={classes["legend__description"]}>{entry.description}</span>
              </Flex>
            ))}
          </Flex>
        </Flex>
      );
    }

    if (!questions.length) {
      return <p className={classes["empty"]}>This stage has no gate questions defined yet.</p>;
    }

    return (
      <Flex
        direction={DIRECTION_CLASS.column}
        alignItems={ALIGN_CLASS.stretch}
        gap={GAP_CLASS.GAP_400}
      >
        <p className={classes["intro"]}>
          These are the questions that you need to answer to move a card from {transitionTitle}.
        </p>

        <Flex
          direction={DIRECTION_CLASS.column}
          alignItems={ALIGN_CLASS.stretch}
          gap={GAP_CLASS.GAP_300}
          className={classes["questions"]}
        >
          {questions.map((question, index) => (
            <Flex
              key={question.id}
              alignItems={ALIGN_CLASS["flex-start"]}
              gap={GAP_CLASS.GAP_300}
              className={classes["question"]}
            >
              <span className={classes["question__index"]}>{index + 1}</span>
              <Flex
                direction={DIRECTION_CLASS.column}
                alignItems={ALIGN_CLASS.stretch}
                gap={GAP_CLASS.GAP_250}
                className={classes["question__content"]}
              >
                <span className={classes["question__text"]}>{question.text}</span>
                <Flex gap={GAP_CLASS.GAP_150} className={classes["question__options"]}>
                  <span className={classes["type_chip"]}>{TYPE_LABELS[question.type]}</span>
                  {question.type === QUESTION_TYPE.MULTIPLE_CHOICE &&
                    question.options?.map((option) => (
                      <span key={option} className={classes["option_chip"]}>
                        {option}
                      </span>
                    ))}
                </Flex>
              </Flex>
            </Flex>
          ))}
        </Flex>
      </Flex>
    );
  };

  return (
    <Modal
      data-testid="gate-preview-modal"
      className="fade-in"
      show
      onDismiss={close}
      backdrop
      centered
    >
      <Modal.Header>
        <b>{transitionTitle}</b>
      </Modal.Header>

      <Modal.Body>
        <Flex
          direction={DIRECTION_CLASS.column}
          alignItems={ALIGN_CLASS.stretch}
          gap={GAP_CLASS.GAP_400}
        >
          {renderBody()}

          <hr className={classes["divider"]} />

          <span className={classes["settings_prompt"]}>
            Need to change these questions? Go to:{" "}
            <Link className={classes["settings_link"]} to={SETTINGS_PATH} onClick={close}>
              Settings → Pipeline Stages
            </Link>
          </span>
        </Flex>
      </Modal.Body>

      <Modal.Footer>
        <NewButton variant={NewButtonVariant.l2} onClick={close}>
          Close
        </NewButton>
      </Modal.Footer>
    </Modal>
  );
};

GatePreviewModal.propTypes = {
  fromTitle: string.isRequired,
  toTitle: string,
  questions: arrayOf(
    shape({
      id: number,
      type: oneOf(["YES_NO", "MULTIPLE_CHOICE", "FREE_TEXT"]).isRequired,
      text: string.isRequired,
      options: arrayOf(string),
    })
  ),
  gateMode: oneOf(["NO_GATE", "SOFT_GATE", "HARD_GATE"]).isRequired,
};

export default GatePreviewModal;
